import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, ModalFooter } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";

import {
  INSTRUCTION_PHOTO_ACCEPT,
  acceptInstructionPhotos,
  revokeInstructionPhotos,
  type InstructionPhotoDraft,
  type InstructionPhotoError,
} from "~/shared/lib/instruction-photos";

import { InstructionPhotoThumb } from "./instruction-photo-thumb";

/**
 * Spelled out rather than interpolated into the key so the translation keys stay
 * statically checkable against the generated locale union.
 */
const ERROR_KEYS = {
  type: "create.photoErrorType",
  size: "create.photoErrorSize",
  count: "create.photoErrorCount",
  total: "create.photoErrorTotal",
} as const;

export interface InstructionPhotosModalProps {
  heldCount: number;
  onSave: (photos: InstructionPhotoDraft[]) => void;
}

export const InstructionPhotosModal: React.FC<InstructionPhotosModalProps> = ({
  heldCount,
  onSave,
}) => {
  const t = useT();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = useState<InstructionPhotoDraft[]>([]);
  const [error, setError] = useState<InstructionPhotoError | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const savedRef = useRef(false);

  useEffect(
    () => () => {
      if (savedRef.current) return; // saved photos are the caller's to revoke now
      revokeInstructionPhotos(draftRef.current);
    },
    [],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setDraft((current) => {
        const { accepted, error: rejection } = acceptInstructionPhotos(
          current,
          files,
          heldCount + current.length,
        );
        setError(rejection);
        return accepted.length > 0 ? [...current, ...accepted] : current;
      });
    },
    [heldCount],
  );

  // Listening on the document rather than a focusable panel: the design invites a
  // bare Ctrl+V, which should work wherever focus happens to be in the modal.
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.files ?? []);
      if (pasted.length === 0) return;
      event.preventDefault();
      addFiles(pasted);
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [addFiles]);

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = ""; // so re-picking the same file still fires a change
    addFiles(picked);
  };

  const handleRemove = (index: number) => {
    URL.revokeObjectURL(draft[index].previewUrl);
    setDraft(draft.filter((_, i) => i !== index));
    setError(null);
  };

  const handleSave = () => {
    savedRef.current = true;
    onSave(draft);
  };

  return (
    <>
      <div className="px-5 py-4">
        {/* A hard height, not a minimum: adding photos must not resize the modal.
            The thumbnails fill the space the empty state already leaves below the
            button, which is why they are the smaller size here. */}
        <div className="flex h-[248px] flex-col items-center gap-3 rounded-lg bg-slate-100 px-4 py-7">
          <p className="max-w-[280px] text-center text-sm leading-snug text-text-secondary">
            {t(Namespace.Orders, "create.photoPasteHint")}
          </p>
          <p className="text-sm text-text-secondary">{t(Namespace.Orders, "create.photoOr")}</p>

          <Button type="button" className="h-9" onClick={() => inputRef.current?.click()}>
            {t(Namespace.Orders, "create.photoPickFromComputer")}
          </Button>

          {draft.length > 0 && (
            <div className="mt-auto flex flex-wrap justify-center gap-2">
              {draft.map((photo, index) => (
                <InstructionPhotoThumb
                  key={photo.previewUrl}
                  src={photo.previewUrl}
                  size="sm"
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Reserved whether or not there's an error, so a rejected file doesn't
            shift the footer either. */}
        <div className="min-h-5 pt-1">
          {error && (
            <p className="text-center text-xs text-brand-red">
              {t(Namespace.Orders, ERROR_KEYS[error])}
            </p>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={INSTRUCTION_PHOTO_ACCEPT}
          multiple
          hidden
          onChange={handlePick}
        />
      </div>

      <ModalFooter>
        <Button
          type="button"
          variant="opacityBlueWithoutOutline"
          className="h-9 disabled:!bg-slate-100 disabled:text-text-secondary disabled:opacity-100"
          disabled={draft.length === 0}
          onClick={handleSave}
        >
          {t(Namespace.Buttons, "save")}
        </Button>
      </ModalFooter>
    </>
  );
};
